import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ScriptLoaderService } from '../../script-loader.service';
import { LocalStorageService } from 'src/app/local-storage.service';
import { WINDOW } from '../../window-providers';
import { LocationService } from '../../location.service';
import { ToastrService } from 'ngx-toastr';
import { IntegrationCompletePayment } from '../+state/payment.actions';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

declare const HostedFields: any;

@Component({
  selector: 'app-clearaccept',
  templateUrl: './clearaccept.component.html',
  styleUrls: ['./clearaccept.component.scss'],
})
export class ClearacceptComponent implements OnInit, OnDestroy {
  paymentWidgetScriptId: string = 'clearaccept-hosted-fields-script';
  paymentWidgetScriptUrl: string =
    'https://sandbox-scripts.clearaccept.systems/hostedfields.1.31.1.js';
  componentsScriptId: string = 'clearaccept-components-script';
  componentsScriptUrl: string =
    'https://sandbox-scripts.clearaccept.systems/components.1.0.17.js';
  componentsScriptIntegrity: string | undefined;
  paymentDetails: any;
  loading: boolean = true;
  isFormValid: boolean = false;
  hostedFieldsInstance: any;
  showApplePayButton: boolean = false;

  constructor(
    private scriptLoaderService: ScriptLoaderService,
    private storageService: LocalStorageService,
    private locationService: LocationService,
    private toastr: ToastrService,
    private store: Store,
    private translate: TranslateService,
    @Inject(WINDOW) private window: Window,
  ) {}

  ngOnInit(): void {
    this.loadPaymentDetails();
    this.loadScriptForComponent();
    this.loadApplePayButton();
  }

  ngOnDestroy(): void {
    this.removeScriptForComponent();
    // Deliberately NOT removing the components script here. It calls
    // customElements.define('ca-apple-pay', ...) at the top level, and custom element
    // definitions are permanent for the page's lifetime — the browser throws
    // NotSupportedError if that script re-executes after a previous run already defined the
    // tag (e.g. the customer navigates back to cart and returns to this payment page).
    // Leaving the script loaded means ScriptLoaderService's loaded-script tracking correctly
    // short-circuits any later loadScript() call for the same URL instead of re-appending it.
  }

  private loadPaymentDetails(): void {
    const paymentDetailJson =
      this.storageService.getSavedState('paymentPayDetails');
    if (!paymentDetailJson) {
      this.window.location.href = this.locationService.base_url('cart');
      return;
    }
    this.paymentDetails = JSON.parse(paymentDetailJson);

    // Check if we should use production script
    if (
      this.paymentDetails?.requestJSON?.paymentParams
        ?.CLEARACCEPT_IS_PRODUCTION === 'true'
    ) {
      this.paymentWidgetScriptUrl =
        'https://scripts.clearaccept.systems/hostedfields.1.31.1.js';
      this.componentsScriptUrl =
        'https://scripts.clearaccept.com/components.1.0.17.js';
      this.componentsScriptIntegrity =
        'sha512-ZPtf5I2BrnzGTrZy5pK4+YKQYWU7Z2IA8uqzps1F7+Npvz64fhCOn7bDZ6c+sh9KK1XywNFRP0m7LslGuu7FyQ==';
    }
  }

  /**
   * Renders ClearAccept's hosted Apple Pay button. This is a separate hosted-components SDK
   * from the hosted card fields above (different script, different web component), so it's
   * loaded and initialised independently — it can fail or be unavailable (e.g. non-Safari
   * browser) without blocking the card-entry form.
   *
   * Important: `buttons.initialise()` scans the page for existing `<ca-apple-pay>` elements
   * and binds to them — the element must already be in the DOM *before* calling it. So
   * `showApplePayButton` is set true up front (to render the element) and only flipped back
   * to false if initialise() rejects, rather than waiting for success to reveal it.
   */
  private loadApplePayButton(): void {
    const paymentRequestId = this.paymentDetails?.requestJSON?.paymentId;
    if (!paymentRequestId) {
      console.error(
        'ClearAccept paymentRequestId is missing, skipping Apple Pay button',
      );
      return;
    }

    this.showApplePayButton = true;

    this.scriptLoaderService
      .loadScript(
        this.componentsScriptUrl,
        this.componentsScriptId,
        this.componentsScriptIntegrity,
      )
      .then(() => {
        const buttons = new (this.window as any).clearaccept.components.buttons(
          {
            paymentRequestId,
          },
        );

        return buttons.initialise().then(() => {
          // Mandatory event handlers per ClearAccept's docs. Without onPaymentCompleted, a
          // completed Apple Pay sheet never gets dismissed (event.complete() is never called);
          // without onError, unexpected mid-payment errors are silently dropped instead of
          // surfaced to the customer.
          buttons.applePay.onPaymentCompleted = (event: any) =>
            this.onApplePayPaymentCompleted(event);
          buttons.applePay.onError = (event: any) => {
            console.error('Apple Pay unhandled error:', event);
            this.toastr.error(
              this.translate.instant(
                'public.clearaccept.errors.applePayFailed',
              ),
            );
          };
        });
      })
      .catch((error: any) => {
        // Most rejections here just mean Apple Pay isn't available for this browser/payment
        // (see ClearAccept's error table — RequestInvalidError, etc.), not a hard failure —
        // the customer still has the manual card form to fall back to.
        console.error('ClearAccept Apple Pay button failed to display:', error);
        this.showApplePayButton = false;
      });
  }

  /**
   * Apple Pay's hosted button confirms the payment with ClearAccept directly (autoConfirm
   * defaults to true), so unlike the manual card form we don't send a token through our own
   * confirm flow. We still tell our backend the attempt finished so it can look up the final
   * status and move the order forward, and separately tell Apple's payment sheet the
   * interaction is done via event.complete() so it can dismiss itself.
   */
  private onApplePayPaymentCompleted(event: any): void {
    const referenceId = this.paymentDetails?.requestJSON?.paymentTransactionId;
    const storeId = this.paymentDetails?.requestJSON?.storeId;

    this.store.dispatch(
      new IntegrationCompletePayment(storeId, referenceId, {}),
    );

    event
      .complete({})
      .catch((error: any) =>
        console.error('Failed to complete Apple Pay payment sheet:', error),
      );
  }

  private loadScriptForComponent(): void {
    this.scriptLoaderService
      .loadScript(this.paymentWidgetScriptUrl, this.paymentWidgetScriptId)
      .then(() => {
        this.initializeClearAccept();
      })
      .catch((error) => {
        console.error(
          `Error loading ClearAccept script: ${this.paymentWidgetScriptUrl}`,
          error,
        );
        this.toastr.error(
          this.translate.instant('public.clearaccept.errors.loadScriptFailed'),
        );
        this.loading = false;
      });
  }

  private initializeClearAccept(): void {
    const fieldToken =
      this.paymentDetails?.requestJSON?.paymentParams
        ?.CLEAR_ACCEPT_FIELD_TOKEN ??
      this.paymentDetails?.requestJSON?.paymentFormParams?.fieldToken;

    if (!fieldToken) {
      console.error('ClearAccept fieldToken is missing');
      this.loading = false;
      return;
    }

    const params = {
      fieldToken: fieldToken,
      options: {
        fields: {
          card: { containerElementId: 'card-wrapper' },
          expdate: { containerElementId: 'expdate-wrapper' },
          cvv: { containerElementId: 'cvv-wrapper' },
        },
        style: {
          logoAlignment: 'right',
          placeholderText: {
            cardNumber: 'Card Number',
            expiryDate: 'MM / YY',
            cvv: 'CVV',
          },
          base: {
            borderColor: '#eaecee',
            borderStyle: 'solid',
            borderWidth: '1px',
            borderRadius: '5px',
            backgroundColor: 'white',
            fontSize: '13px',
            fontColor: '#23313e',
            fontWeight: 'normal',
            textAlign: 'left',
            padding: '10px',
            focus: { borderColor: '#007bff' },
            hover: { borderColor: '#eaecee' },
          },
        },
      },
    };

    (this.window as any).HostedFields.initialise(
      params.fieldToken,
      params.options,
      (error: any, hostedFields: any) => {
        this.loading = false;
        if (error) {
          console.error('ClearAccept initialization error:', error);
          this.toastr.error(
            this.translate.instant(
              'public.clearaccept.errors.initFieldsFailed',
            ),
          );
          return;
        }

        this.hostedFieldsInstance = hostedFields;

        this.hostedFieldsInstance.on('change', (formStatus: any) => {
          this.isFormValid = formStatus.isValid;

          const cardErrorContainer = document.getElementById('card-errors');
          const expdateErrorContainer =
            document.getElementById('expdate-errors');
          const cvvErrorContainer = document.getElementById('cvv-errors');

          this.setValidationStatus(
            formStatus,
            formStatus.card,
            cardErrorContainer,
          );
          this.setValidationStatus(
            formStatus,
            formStatus.expDate,
            expdateErrorContainer,
          );
          if (cvvErrorContainer) {
            this.setValidationStatus(
              formStatus,
              formStatus.cvv,
              cvvErrorContainer,
            );
          }
        });
      },
    );
  }

  private setValidationStatus(
    formStatus: any,
    fieldStatus: any,
    container: HTMLElement,
  ): void {
    if (!container) return;
    if (
      fieldStatus.isValid ||
      (fieldStatus.isPristine && formStatus.isNotSubmited)
    ) {
      container.textContent = '';
    } else {
      container.textContent =
        fieldStatus.message ||
        this.translate.instant('public.clearaccept.errors.invalidField');
    }
  }

  submitPayment(): void {
    if (!this.isFormValid || this.loading) return;

    this.loading = true;
    const nameInput = document.getElementById('cc_name') as HTMLInputElement;
    const data = {
      accountHolderName: nameInput ? nameInput.value : '',
    };

    this.hostedFieldsInstance.setData(data);
    this.hostedFieldsInstance.tokenize((token: any) => {
      if (token.TokenId) {
        this.onPaymentTokenReceived(token.TokenId);
      } else {
        this.loading = false;
        this.toastr.error(
          this.translate.instant(
            'public.clearaccept.errors.tokenizationFailed',
          ),
        );
      }
    });
  }

  private onPaymentTokenReceived(tokenId: string): void {
    const referenceId = this.paymentDetails?.requestJSON?.paymentTransactionId;
    const storeId = this.paymentDetails?.requestJSON?.storeId;

    this.store.dispatch(
      new IntegrationCompletePayment(storeId, referenceId, {
        token: tokenId,
      }),
    );
  }

  getAmount(): number {
    return this.paymentDetails?.requestJSON?.paymentFormParams?.amount || 0;
  }

  private removeScriptForComponent(): void {
    this.scriptLoaderService.removeScriptById(this.paymentWidgetScriptId);
  }
}
